'use client';

import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { usePropertyMapKey } from '@/src/lib/property-map/usePropertyMapKey';
import { BELOW_FOLD_ACTIVATION_ROOT_MARGIN, useViewportActivation } from '@/src/lib/hooks/ui';
import type { PropertyDetailListing } from '../types';
import { Module } from '../ui/Module/Module';
import { getMappedUnits } from './propertyMap.utils';
import { usePropertyMapTracking } from './PropertyMapAnalytics';
import { PropertyMapSkeleton } from './PropertyMapSkeleton';
import * as styles from './propertyMapModule.styles';

const PropertyMap = dynamic(() => import('./PropertyMap').then((module) => module.PropertyMap), {
  ssr: false,
  loading: () => <PropertyMapSkeleton />,
});

export function PropertyMapModule({ listing }: { listing: PropertyDetailListing }) {
  const { apiKey, isPending } = usePropertyMapKey(getMappedUnits(listing).length > 0);
  if (!apiKey && !isPending) return null;
  return (
    <PropertyMapSection
      key={`${listing.aliasEncoded ?? listing.maloneLotIdEncoded}:${listing.propertyMap?.mapId}`}
      listing={listing}
      apiKey={apiKey}
    />
  );
}

function PropertyMapSection({ listing, apiKey }: { listing: PropertyDetailListing; apiKey: string }) {
  const t = useTranslations('hdp');
  const { entryRef } = usePropertyMapTracking('home_details');
  const { ref, isActive } = useViewportActivation<HTMLDivElement>({ rootMargin: BELOW_FOLD_ACTIVATION_ROOT_MARGIN });
  return (
    <Module id="property-map-module" title={t('propertyMapTitle')} headingRef={apiKey ? entryRef : undefined}>
      <div ref={ref} className={styles.container}>
        {isActive && apiKey ? <PropertyMap listing={listing} apiKey={apiKey} /> : <PropertyMapSkeleton />}
      </div>
    </Module>
  );
}
